import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { TabNavigationService, SearchSettings } from '~/services/tab-navigation.service';
import { ChatMessage, ChatDocumentCard, DrtEditableField, DrtEditablePayload, DrtEditableSection } from './models/chat-message.model';
import { ChatFeedbackEvent, DrtGenerateEvent, EwoShowMoreEvent } from './chat-interface/chat-interface.component';
import { ChatService, DrtProgressStep, FeedbackRequestPayload, WebSocketEvent } from './services/chat.service';
import { ConversationHistoryService, ConversationHistoryItem } from './services/conversation-history.service';

interface DataSource {
    label: string;
    selected: boolean;
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    private readonly PENDING_THREAD_ID = 'new';
    private readonly DEFAULT_PROMPT_PLACEHOLDER = 'Ask IntelENGINE';
    // Chat state
    public isChatMode: boolean = false;
    public chatMessages: ChatMessage[] = [];
    public animatedWelcomeText: string = '';
    public isAnimating: boolean = false;
    public animationComplete: boolean = false;

    // Input state
    public promptText: string = '';
    public selectedFiles: string[] = [];
    private selectedFileObjects: File[] = [];
    public showDataSources: boolean = false;
    public feedbackDialogOpen: boolean = false;
    public dataSources: DataSource[] = [
        { label: 'CES', selected: true },
        { label: 'ESRAA', selected: true },
        { label: 'SHAREPOINT', selected: true }
    ];

    // Loading state
    public isLoading: boolean = false;
    public drtProgress: DrtProgressStep[] = [];

    // Follow-up suggestions state
    public suggestionsDisabled: boolean = false;
    // Tracks the agent used in the latest response (for follow-up API)
    private lastAgentUsed: string | null = null;

    // Thread management for conversation
    private currentThreadId: string = '';
    private chatHistory: [string, string][] = [];
    private latestRouting: any = null;
    private pendingPrompt: string | null = null;
    private pendingDrtPayload: {
        pdf_file_b64?: string;
        image_file_b64?: string;
        verified_process_info?: Record<string, string>;
        scope_parameters?: Record<string, string>;
        drt_parameters_data?: any;
        search_more_ewos?: boolean;
        source_conversation_id?: string;
        top_k?: number;
    } | null = null;
    private lastDrtPdfFileB64: string | null = null;
    private hasActiveRequest: boolean = false;
    private isReconnectingSocket: boolean = false;
    private hasReceivedResponseForActiveRequest: boolean = false;
    private processedResponseFingerprints = new Set<string>();
    
    // Queue-based reconnection state
    private needsReconnection: boolean = false;
    private pendingThreadId: string = '';
    /** Thread whose socket rebind is postponed until the current turn finishes streaming. */
    private deferredSocketRebindThreadId: string = '';

    // Component properties
    private fullText: string = 'Welcome to IntelENGINE Chatbot';
    private animationSpeed: number = 50;
    private animationInterval: ReturnType<typeof setInterval> | null = null;
    private destroy$ = new Subject<void>();
    private websocketSubscription: Subscription | null = null;
    private searchSettings: SearchSettings = {
        numberOfDocuments: 6,
        searchType: 'quick',
        selectedAgent: 'auto',
        generateDrtTable: false
    };

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private location: Location,
        private tabNavigationService: TabNavigationService,
        private chatService: ChatService,
        private conversationHistoryService: ConversationHistoryService
    ) {}

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public ngOnInit(): void {
        // Subscribe to paramMap observable so the component reacts to thread changes without reloading.
        this.route.paramMap
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {
                const threadId = params.get('threadId');
                if (threadId) {
                    // Skip reload if the same thread is already loaded.
                    if (threadId !== this.currentThreadId) {
                        this.clearChatState();
                        this.loadThreadFromApi(threadId);
                    }
                } else {
                    // No threadId in URL — show welcome view.
                    this.resetChatView();
                }
            });

        // Listen for "New Chat" button in sidebar
        this.tabNavigationService.newChat$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.resetChatView(true);
            });

        this.tabNavigationService.searchSettings$
            .pipe(takeUntil(this.destroy$))
            .subscribe((settings) => {
                this.searchSettings = settings;
            });
    }

    public ngOnDestroy(): void {
        if (this.animationInterval !== null) {
            clearInterval(this.animationInterval);
            this.animationInterval = null;
        }
        this.websocketSubscription?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Close dropdown when clicking outside
     */
    @HostListener('document:click')
    public closeDropdownOnOutsideClick(): void {
        if (this.showDataSources) {
            this.showDataSources = false;
        }
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************

    /**
     * Clear in-memory chat state without triggering navigation or animation.
     * Called when the user switches to a different history thread while already
     * in an active chat — wipes old messages so the new thread loads into a clean view.
     */
    private clearChatState(): void {
        this.chatMessages = [];
        this.chatHistory = [];
        this.promptText = '';
        this.selectedFiles = [];
        this.selectedFileObjects = [];
        this.isLoading = true;
        this.currentThreadId = '';
        this.latestRouting = null;
        this.pendingPrompt = null;
        this.pendingDrtPayload = null;
        this.lastDrtPdfFileB64 = null;
        this.hasActiveRequest = false;
        this.isReconnectingSocket = false;
        this.needsReconnection = false;
        this.pendingThreadId = '';
        this.hasReceivedResponseForActiveRequest = false;
        this.processedResponseFingerprints.clear();
        this.isChatMode = true;
        this.suggestionsDisabled = false;
        this.lastAgentUsed = null;
    }

    /**
     * Reset chat view to initial welcome state when user clicks "New Chat" in sidebar
     */
    public resetChatView(navigate: boolean = false): void {
        if (navigate) {
            this.router.navigate(['/'], { replaceUrl: true });
        }
        this.isChatMode = false;
        this.chatMessages = [];
        this.promptText = '';
        this.selectedFiles = [];
        this.selectedFileObjects = [];
        this.isLoading = false;
        this.currentThreadId = '';
        this.chatHistory = [];
        this.latestRouting = null;
        this.pendingPrompt = null;
        this.pendingDrtPayload = null;
        this.lastDrtPdfFileB64 = null;
        this.hasActiveRequest = false;
        this.isReconnectingSocket = false;
        this.needsReconnection = false;
        this.pendingThreadId = '';
        this.hasReceivedResponseForActiveRequest = false;
        this.processedResponseFingerprints.clear();
        this.initializeWebSocketForCurrentThread(this.PENDING_THREAD_ID);
        this.animateWelcomeText();
    }

    /**
     * Handle example question selection
     * Sets the prompt text and focuses textarea
     */
    public onQuestionClick(questionText: string): void {
        this.promptText = questionText;
        
        // Scroll to and focus input
        setTimeout(() => {
            const textarea = document.querySelector('.chat-text') as HTMLTextAreaElement;
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(questionText.length, questionText.length);
            }
        }, 100);
    }

    /**
     * Handle prompt text input changes.
     * Disable suggestion chips as soon as the user starts typing.
     */
    public onPromptChange(text: string): void {
        this.promptText = text;
        if (text.length > 0) {
            this.suggestionsDisabled = true;
        }
    }

    /**
     * Handle file selection
     */
    public onFileSelected(fileList: FileList): void {
        if (fileList && fileList.length > 0) {
            const files = Array.from(fileList);
            const names = files.map(f => f.name);
            this.selectedFileObjects = [...this.selectedFileObjects, ...files];
            this.selectedFiles = [...this.selectedFiles, ...names];
        }
    }

    /**
     * Remove file by index
     */
    public removeFile(index: number): void {
        if (index > -1 && index < this.selectedFiles.length) {
            this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
            this.selectedFileObjects = this.selectedFileObjects.filter((_, i) => i !== index);
        }
    }

    /**
     * Handle data source dropdown toggle
     */
    public toggleDataSources(event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        this.showDataSources = !this.showDataSources;
    }

    /**
     * Handle individual data source toggle
     */
    public toggleSource(source: DataSource): void {
        source.selected = !source.selected;
    }

    /**
     * Close dropdown when clicking outside
     */
    public onDropdownClick(event: MouseEvent): void {
        event.stopPropagation();
    }

    /**
     * Close dropdown on document click
     */
    public closeDropdown(): void {
        this.showDataSources = false;
    }

    public get promptPlaceholder(): string {
        return this.DEFAULT_PROMPT_PLACEHOLDER;
    }

    public get isFileUploadEnabled(): boolean {
        return true;
    }

    /**
     * Main submit handler - Called when user clicks "Analyze" button
     */
    public onSubmit(): void {
        if (this.promptText.trim().length === 0 && this.selectedFiles.length === 0) {
            return;
        }

        // Switch to chat mode
        this.isChatMode = true;

        // Create user message object
        const userMessage: ChatMessage = {
            id: this.generateId(),
            text: this.promptText,
            sender: 'user',
            timestamp: new Date(),
            type: 'text',
            files: this.selectedFiles.length > 0 ? this.selectedFiles : undefined
        };

        // Add to chat history
        this.chatMessages.push(userMessage);
        this.persistMessagesToStorage();

        // Store for API call
        const promptToSend = this.promptText;
        const filesToSend = [...this.selectedFileObjects];

        // Clear input
        this.promptText = '';
        this.selectedFiles = [];
        this.selectedFileObjects = [];

        // Disable suggestion chips before sending so old chips grey out immediately
        this.suggestionsDisabled = true;

        void this.sendMessageViaWebSocket(promptToSend, filesToSend);
    }

    /**
     * Handle a follow-up suggestion chip click.
     * Pre-fills the input and auto-submits.
     */
    public onSuggestionClicked(suggestion: string): void {
        if (this.suggestionsDisabled) { return; }
        this.promptText = suggestion;
        this.onSubmit();
    }

    //*************************************************************************
    //  Private Methods
    //*************************************************************************

    /**
     * Load thread messages from API conversation history
     * Called when a thread ID is provided in the route
     */
    private loadThreadFromApi(threadId: string): void {
        this.isLoading = true;

        const hasThreadCache = this.conversationHistoryService.hasThreadConversation(threadId);
        if (hasThreadCache) {
            const cachedMessages = this.conversationHistoryService.getThreadMessages(threadId);
            if (cachedMessages && cachedMessages.length > 0) {
                this.loadThreadMessages(threadId, cachedMessages);
                this.isLoading = false;
                return;
            }
        }

        this.conversationHistoryService.fetchThreadConversation(threadId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    console.log('✅ [HOME] Thread conversation loaded');
                    const messages = this.conversationHistoryService.getThreadMessages(threadId);
                    
                    if (messages && messages.length > 0) {
                        this.loadThreadMessages(threadId, messages);
                        this.isLoading = false;
                    } else {
                        console.warn('⚠️ [HOME] No messages found for thread:', threadId);
                        this.router.navigate(['/'], { replaceUrl: true });
                        this.resetChatView();
                    }
                },
                error: (error) => {
                    console.error('❌ [HOME] Error loading conversation history:', error);
                    // Fallback to localStorage
                    this.restoreThreadFromStorage(threadId);
                    this.isLoading = false;
                }
            });
    }

    /**
     * Load and display thread messages
     * Reuses existing markdown rendering path in templates and sets up chat interface
     */
    private loadThreadMessages(threadId: string, historyItems: ConversationHistoryItem[]): void {
        this.currentThreadId = threadId;
        this.isChatMode = true;
        this.animationComplete = true;
        this.chatMessages = [];
        this.chatHistory = [];

        let previousWasDrtHitl = false;
        for (const item of historyItems) {
            let hitlPayload: any = null;
            if (typeof item.ai_response === 'string' && item.ai_response.includes('"_drt_hitl"')) {
                try { hitlPayload = JSON.parse(item.ai_response); } catch { hitlPayload = null; }
            } else if (item.ai_response && typeof item.ai_response === 'object' && (item.ai_response as any)._drt_hitl) {
                hitlPayload = item.ai_response;
            }

            // The result turn repeats the HITL turn's query; avoid a duplicate user bubble.
            if (!(previousWasDrtHitl && !hitlPayload)) {
                this.chatMessages.push({
                    text: item.user_input,
                    sender: 'user',
                    timestamp: new Date(item.timestamp),
                    type: 'text',
                    threadId: threadId
                });
            }
            previousWasDrtHitl = !!hitlPayload;

            if (hitlPayload) {
                this.chatMessages.push({
                    id: `${threadId}_${item.conversation_id}`,
                    text: '',
                    sender: 'bot',
                    timestamp: new Date(item.timestamp),
                    type: 'drt-parameters',
                    drtParameters: this.buildDrtEditablePayload(hitlPayload),
                    drtRawData: hitlPayload,
                    // Pending flag makes the card read-only and hides "Confirm and proceed".
                    drtGeneratePending: true,
                    threadId,
                    agent: 'drt',
                    conversationId: item.conversation_id
                });
                continue;
            }

            const responseData = {
                response: typeof item.ai_response === 'string' ? item.ai_response : (item.response || ''),
                ai_response: item.ai_response,
                agent_used: item.agent_used,
                routing: item.routing,
                primary_agent: item.primary_agent,
                secondary_agents: item.secondary_agents,
                confidence_score: item.confidence_score,
                confidence_reasoning: item.confidence_reasoning,
                citations: item.citations || {},
                feedback: item.feedback
            };

            const botMessage = this.buildBotMessageFromResponseData(
                responseData,
                new Date(item.timestamp),
                threadId
            );
            botMessage.conversationId = item.conversation_id;
            this.chatMessages.push(botMessage);

            // Add to chat history for API calls
            this.chatHistory.push([item.user_input, this.normalizeIncomingMarkdown(responseData.response)]);
        }

        // Persist to localStorage for offline access
        this.persistMessagesToStorage(threadId);

        // Connect WebSocket so user can continue the conversation
        this.initializeWebSocketForCurrentThread(threadId);
    }

    /**
     * Restore a previous chat session from localStorage using threadId from URL.
     * Called on page refresh when URL is /chat/:threadId.
     */
    private restoreThreadFromStorage(threadId: string): void {
        const stored = localStorage.getItem(`chat_messages_${threadId}`);
        if (stored) {
            try {
                const parsed = JSON.parse(stored) as ChatMessage[];
                // Re-hydrate Date objects lost during JSON serialization
                this.chatMessages = parsed.map(msg => {
                    const { htmlContent, ...rest } = msg as ChatMessage & { htmlContent?: any };
                    return { ...rest, timestamp: new Date(msg.timestamp) };
                });
                this.isChatMode = true;
                this.currentThreadId = threadId;
                this.animationComplete = true;

                // Rebuild chat history for future API calls
                this.chatHistory = [];
                for (let i = 0; i + 1 < this.chatMessages.length; i++) {
                    const curr = this.chatMessages[i];
                    const next = this.chatMessages[i + 1];
                    if (curr.sender === 'user' && next.sender === 'bot') {
                        this.chatHistory.push([curr.text, next.text]);
                        i++;
                    }
                }

                // Reconnect WebSocket so user can continue the conversation
                this.initializeWebSocketForCurrentThread(threadId);
            } catch (e) {
                this.router.navigate(['/'], { replaceUrl: true });
                this.resetChatView();
            }
        } else {
            if (this.currentThreadId === threadId && this.chatMessages.length > 0) {
                this.isChatMode = true;
                return;
            }

            // No saved session for this threadId — redirect home
            this.router.navigate(['/'], { replaceUrl: true });
            this.resetChatView();
        }
    }

    /** Updates the browser URL for the active thread without triggering Angular navigation. */
    private navigateToThread(threadId: string): void {
        if (!threadId || threadId === this.PENDING_THREAD_ID) {
            return;
        }
        const targetUrl = `/chat/${threadId}`;
        const currentPath = this.location.path().split('?')[0];
        // Use Location path because router.url does not change after replaceState.
        if (currentPath !== targetUrl) {
            this.location.replaceState(targetUrl);
        }
    }

    /**
     * Persist current chatMessages to localStorage keyed by threadId.
     * Called after every message push when a real threadId is known.
     */
    private persistMessagesToStorage(threadId?: string): void {
        const resolvedThreadId = (threadId || this.currentThreadId || '').trim();

        if (!resolvedThreadId || resolvedThreadId === this.PENDING_THREAD_ID) {
            return;
        }

        try {
            // conversationPendingSave is intentionally dropped: the save has
            // completed server-side by the time a stored thread is restored.
            const serializableMessages = this.chatMessages.map(({ htmlContent, drtSourcePdfFileB64, conversationPendingSave, ...rest }) => ({
                ...rest,
                threadId: rest.threadId || resolvedThreadId
            }));
            localStorage.setItem(`chat_messages_${resolvedThreadId}`, JSON.stringify(serializableMessages));
        } catch (e) {
            // Storage error - silently fail
        }
    }

    private bindThreadId(threadId: string): void {
        const resolvedThreadId = (threadId || '').trim();
        if (!resolvedThreadId || resolvedThreadId === this.PENDING_THREAD_ID) {
            return;
        }

        this.currentThreadId = resolvedThreadId;
        this.chatMessages = this.chatMessages.map(message => ({
            ...message,
            threadId: message.threadId || resolvedThreadId
        }));
        this.persistMessagesToStorage(resolvedThreadId);
        this.navigateToThread(resolvedThreadId);
    }

    /** Switches the active WebSocket to the resolved thread immediately after receiving a real thread_id. */
    private reconnectWebSocketToThread(threadId: string): void {
        const resolvedThreadId = (threadId || '').trim();
        if (!resolvedThreadId || resolvedThreadId === this.PENDING_THREAD_ID) {
            return;
        }

        this.needsReconnection = false;
        this.pendingThreadId = '';
        this.isReconnectingSocket = true;
        this.initializeWebSocketForCurrentThread(resolvedThreadId);
    }

    /** Performs a thread rebind that was postponed until the current turn finished streaming. */
    private flushDeferredSocketRebind(): void {
        const threadId = this.deferredSocketRebindThreadId;
        if (!threadId) {
            return;
        }
        this.deferredSocketRebindThreadId = '';
        this.reconnectWebSocketToThread(threadId);
    }

    /**
     * Initialize websocket stream listener for current thread
     */
    private initializeWebSocketForCurrentThread(threadId?: string): void {
        this.websocketSubscription?.unsubscribe();

        this.websocketSubscription = this.chatService.connectWebSocket(threadId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((event: WebSocketEvent) => {
                this.handleWebSocketEvent(event);
            });
    }

    /** Waits for WebSocket reconnection to pending thread before sending messages to prevent race conditions. */
    private async ensureWebSocketReconnected(): Promise<void> {
        if (!this.needsReconnection || !this.pendingThreadId) {
            return;
        }
        
        return new Promise((resolve) => {
            // Set up temporary listener for connected event
            const tempSub = this.chatService.connectWebSocket(this.pendingThreadId)
                .pipe(
                    filter((e: WebSocketEvent) => e.type === 'connected'),
                    takeUntil(this.destroy$)
                )
                .subscribe(() => {
                    console.log(`[WS-RECONNECT] Connection established to thread: ${this.pendingThreadId}`);
                    tempSub.unsubscribe();
                    this.needsReconnection = false;
                    resolve();
                });

            // Also subscribe to handle events (replaces initializeWebSocketForCurrentThread)
            this.websocketSubscription?.unsubscribe();
            this.websocketSubscription = this.chatService.connectWebSocket(this.pendingThreadId)
                .pipe(takeUntil(this.destroy$))
                .subscribe((event: WebSocketEvent) => {
                    this.handleWebSocketEvent(event);
                });
        });
    }

    /**
     * Send chat message over WebSocket.
     * Supports multiple files: each file is sent as a separate WebSocket message.
     * PDFs → pdf_file_b64, PNG/JPG/JPEG → image_file_b64.
     * The text prompt is sent with the first file; subsequent files use an empty prompt.
     */
    private async sendMessageViaWebSocket(prompt: string, uploadedFiles: File[] = []): Promise<void> {
        // Ensure WebSocket is reconnected if needed (queue-based approach)
        if (this.needsReconnection && this.pendingThreadId) {
            await this.ensureWebSocketReconnected();
        }

        this.isLoading = true;
        this.hasActiveRequest = true;
        this.hasReceivedResponseForActiveRequest = false;
        this.processedResponseFingerprints.clear();

        const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']);
        const IMAGE_EXTS  = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];

        const isImageFile = (f: File): boolean =>
            IMAGE_TYPES.has(f.type) || IMAGE_EXTS.some(ext => f.name.toLowerCase().endsWith(ext));

        const isPdfFile = (f: File): boolean =>
            f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');

        // Build one payload entry per file
        type FilePayload = { pdf_file_b64?: string; image_file_b64?: string; pdf_file_name?: string; image_file_name?: string };
        const filePayloads: FilePayload[] = [];

        for (const file of uploadedFiles) {
            try {
                const b64 = await this.fileToBase64(file);
                if (isPdfFile(file)) {
                    this.lastDrtPdfFileB64 = b64;
                    filePayloads.push({ pdf_file_b64: b64, pdf_file_name: file.name });
                } else if (isImageFile(file)) {
                    filePayloads.push({ image_file_b64: b64, image_file_name: file.name });
                }
                // unsupported file types are silently skipped
            } catch {
                this.handleApiError({ message: `Unable to read the uploaded file: ${file.name}` });
                return;
            }
        }

        if (filePayloads.length === 0) {
            // No files or no supported files — send text-only message
            const sent = this.chatService.sendMessageOverWebSocket(
                prompt,
                this.chatHistory,
                this.currentThreadId,
                this.buildUserContext(),
                undefined
            );
            if (!sent) {
                this.pendingPrompt = prompt;
                this.pendingDrtPayload = null;
                this.isReconnectingSocket = true;
                this.initializeWebSocketForCurrentThread(this.currentThreadId || this.PENDING_THREAD_ID);
            }
            return;
        }

        // Send one WS message per file.
        // First file carries the text prompt; subsequent files send with empty prompt
        // so the backend processes each file in the context of this conversation.
        for (let i = 0; i < filePayloads.length; i++) {
            const messageText = i === 0 ? prompt : '';
            const drtPayload = filePayloads[i];
            const sent = this.chatService.sendMessageOverWebSocket(
                messageText,
                this.chatHistory,
                this.currentThreadId,
                this.buildUserContext(),
                drtPayload
            );
            if (!sent) {
                this.pendingPrompt = messageText;
                this.pendingDrtPayload = drtPayload;
                this.isReconnectingSocket = true;
                this.initializeWebSocketForCurrentThread(this.currentThreadId || this.PENDING_THREAD_ID);
                return; // stop sending further files if WS not connected
            }
        }
    }
    private handleWebSocketEvent(event: WebSocketEvent): void {
        if (!event || !event.type) {
            return;
        }

        if (['response', 'drt_parameters', 'drt_result', 'complete', 'error', 'auth_failed'].includes(event.type)) {
            this.drtProgress = [];
        }

        switch (event.type) {
            case 'connected':
                console.log('✅ [WS-EVENT] WebSocket connected for thread:', event.data?.thread_id);
                this.isReconnectingSocket = false;
                break;

            case 'auth_success':
                console.log('✅ [WS-EVENT] Authentication successful:', event.data);
                
                // Now that we're authenticated, send any pending messages
                if (this.pendingPrompt !== null || this.pendingDrtPayload) {
                    const promptToSend = this.pendingPrompt || '';
                    this.pendingPrompt = null;
                    const resent = this.chatService.sendMessageOverWebSocket(
                        promptToSend,
                        this.chatHistory,
                        this.currentThreadId,
                        this.buildUserContext(),
                        this.pendingDrtPayload || undefined
                    );

                    this.pendingDrtPayload = null;

                    if (!resent && this.hasActiveRequest) {
                        this.handleApiError({ message: 'Failed to send message over WebSocket.' });
                    }
                }
                break;

            case 'auth_failed':
                console.error('❌ [WS-EVENT] Authentication failed:', event.data?.error);
                this.isLoading = false;
                this.hasActiveRequest = false;
                this.pendingPrompt = null;
                this.pendingDrtPayload = null;
                
                // Show error to user
                this.handleApiError({ 
                    message: event.data?.error || 'Authentication failed. Please refresh and try again.' 
                });
                break;

            case 'drt_progress': {
                const p = event.data || {};
                if (!p.step) {
                    break;
                }
                const idx = this.drtProgress.findIndex(s => s.step === p.step);
                const entry: DrtProgressStep = { step: p.step, label: p.label || p.step, status: p.status || 'progress', detail: p.detail };
                this.drtProgress = idx >= 0
                    ? this.drtProgress.map((s, i) => (i === idx ? entry : s))
                    : [...this.drtProgress, entry];
                break;
            }

            case 'routing':
                this.latestRouting = event.data;
                break;

            case 'agent_processing':
                this.isLoading = true;
                break;

            case 'response': {
                if (!this.hasActiveRequest && !this.pendingPrompt) {
                    break;
                }

                const responseData = event.data || {};
                const responseThreadId = responseData.thread_id || responseData?.data?.thread_id;
                const formattedResponse = this.normalizeIncomingMarkdown(responseData.response);

                const responseFingerprint = this.buildResponseFingerprint(responseThreadId, formattedResponse, responseData.agent_used);
                if (this.processedResponseFingerprints.has(responseFingerprint)) {
                    break;
                }
                this.processedResponseFingerprints.add(responseFingerprint);
                this.hasReceivedResponseForActiveRequest = true;

                if (responseThreadId && this.currentThreadId !== responseThreadId) {
                    // Bind the real thread_id now, but swap the socket only after
                    // the turn's remaining events (conversation_saved, complete)
                    // have been delivered on the current connection.
                    this.bindThreadId(responseThreadId);
                    this.deferredSocketRebindThreadId = responseThreadId;
                }

                const lastUserMessage = this.chatMessages[this.chatMessages.length - 1];

                if (lastUserMessage && lastUserMessage.sender === 'user') {
                    this.chatHistory.push([lastUserMessage.text, formattedResponse]);
                }

                const botMessage = this.buildBotMessageFromResponseData(
                    responseData,
                    new Date(),
                    this.currentThreadId || this.PENDING_THREAD_ID,
                    true
                );
                botMessage.conversationId = responseData?.conversation_id;
                // The backend persists this turn in the background; feedback and
                // show-more need the stored record, so keep them hidden until
                // the conversation_saved event arrives.
                botMessage.conversationPendingSave = !!responseData?.conversation_id;

                const shouldSkipDrtDuplicate =
                    botMessage.type === 'drt-result-table'
                    && this.hasExistingDrtResultMessage(
                        this.currentThreadId || this.PENDING_THREAD_ID,
                        botMessage.documents?.[0]?.drtParameters
                    );

                this.lastAgentUsed = responseData?.agent_used || null;
                if (!shouldSkipDrtDuplicate) {
                    this.chatMessages.push(botMessage);
                }
                this.persistMessagesToStorage();
                this.isLoading = false;
                this.hasActiveRequest = false;
                if (!shouldSkipDrtDuplicate) {
                    this.fetchAndAttachSuggestions(this.chatMessages.length - 1);
                }
                break;
            }

            case 'conversation_saved': {
                const savedData = event.data || {};
                if (!savedData.saved || !savedData.conversation_id) {
                    break;
                }

                const savedMessage = this.chatMessages.find(
                    message => message.conversationId === savedData.conversation_id
                );
                if (savedMessage) {
                    savedMessage.conversationPendingSave = false;
                    this.persistMessagesToStorage();
                }
                break;
            }

            case 'drt_parameters': {
                const drtData = event.data || {};
                const drtThreadId = drtData.thread_id;

                if (drtThreadId && this.currentThreadId !== drtThreadId) {
                    this.bindThreadId(drtThreadId);
                }

                const drtParameters = this.buildDrtEditablePayload(drtData);

                this.chatMessages.push({
                    id: `${(this.currentThreadId || this.PENDING_THREAD_ID)}_${Date.now()}`,
                    text: '',
                    sender: 'bot',
                    timestamp: new Date(),
                    type: 'drt-parameters',
                    drtParameters,
                    drtRawData: drtData,
                    drtSourcePdfFileB64: this.lastDrtPdfFileB64 || undefined,
                    threadId: this.currentThreadId || this.PENDING_THREAD_ID,
                    agent: 'drt'
                });

                this.lastAgentUsed = 'drt';
                this.appendLatestTurnToChatHistory(this.buildDrtParametersSummary(drtParameters));

                this.isLoading = false;
                this.hasActiveRequest = false;
                this.hasReceivedResponseForActiveRequest = false;
                this.processedResponseFingerprints.clear();
                this.persistMessagesToStorage();
                break;
            }

            case 'drt_result': {
                const drtResult = event.data || {};
                const drtThreadId = drtResult.thread_id;

                if (drtThreadId && this.currentThreadId !== drtThreadId) {
                    this.bindThreadId(drtThreadId);
                }

                const drtTablePayload = this.extractGeneratedDrtTablePayload(drtResult);
                const hasDrtTable = drtResult?.status === 'success' && !!drtTablePayload;
                const messageThreadId = this.currentThreadId || this.PENDING_THREAD_ID;

                if (hasDrtTable) {
                    const shouldSkipDrtDuplicate = this.hasExistingDrtResultMessage(
                        messageThreadId,
                        drtTablePayload
                    );

                    if (!shouldSkipDrtDuplicate) {
                        this.chatMessages.push({
                            id: `${messageThreadId}_${Date.now()}`,
                            text: 'Generated DRT Summary',
                            sender: 'bot',
                            timestamp: new Date(),
                            type: 'drt-result-table',
                            documents: [
                                {
                                    id: `drt_result_${Date.now()}`,
                                    title: 'Generated DRT',
                                    date: new Date().toLocaleDateString(),
                                    documentType: 'EWO',
                                    summary: 'Generated DRT output',
                                    selected: true,
                                    drtParameters: drtTablePayload
                                }
                            ],
                            threadId: messageThreadId,
                            agent: 'drt'
                        });

                        this.appendLatestTurnToChatHistory('Generated DRT Summary');
                    }
                } else {
                    const resultText = this.normalizeIncomingMarkdown(
                        drtResult.response || drtResult.message || drtResult.data || drtResult
                    );

                    this.chatMessages.push({
                        id: `${(this.currentThreadId || this.PENDING_THREAD_ID)}_${Date.now()}`,
                        text: resultText,
                        sender: 'bot',
                        timestamp: new Date(),
                        type: 'text',
                        isMarkdown: true,
                        threadId: this.currentThreadId || this.PENDING_THREAD_ID,
                        agent: 'drt'
                    });

                    this.appendLatestTurnToChatHistory(resultText);
                }

                this.lastAgentUsed = 'drt';

                this.isLoading = false;
                this.latestRouting = null;
                this.hasActiveRequest = false;
                this.hasReceivedResponseForActiveRequest = false;
                this.processedResponseFingerprints.clear();
                this.persistMessagesToStorage();
                this.fetchAndAttachSuggestions(this.chatMessages.length - 1);
                break;
            }

            case 'complete':
                if (event.data?.thread_id && this.currentThreadId !== event.data.thread_id) {
                    this.bindThreadId(event.data.thread_id);
                }
                this.isLoading = false;
                this.latestRouting = null;
                this.hasActiveRequest = false;
                this.hasReceivedResponseForActiveRequest = false;
                this.processedResponseFingerprints.clear();
                this.persistMessagesToStorage();
                this.flushDeferredSocketRebind();
                break;

            case 'error':
                if (this.hasActiveRequest) {
                    const errorText = String(event.data?.error || event.data?.message || '').toLowerCase();
                    const isSocketNotConnected = errorText.includes('websocket is not connected');
                    const hasPendingRetry = this.pendingPrompt !== null || !!this.pendingDrtPayload;

                    // Ignore transient send errors while a queued retry is pending.
                    if (isSocketNotConnected && hasPendingRetry) {
                        break;
                    }
                    this.handleApiError(event.data);
                }
                this.flushDeferredSocketRebind();
                break;

            case 'disconnected':
                if (this.isReconnectingSocket) {
                    break;
                }

                if (this.hasActiveRequest && !this.hasReceivedResponseForActiveRequest) {
                    this.handleApiError({ message: 'WebSocket disconnected during request.' });
                }
                this.flushDeferredSocketRebind();
                break;

            default:
                break;
        }
    }

    /**
     * Call /followup/suggestions after a bot response completes and attach the
     * returned suggestions to the last bot text message in the chat.
     */
    private fetchAndAttachSuggestions(targetIndex?: number): void {
        if (this.chatHistory.length === 0) { return; }

        let resolvedTargetIndex = typeof targetIndex === 'number' ? targetIndex : -1;
        if (resolvedTargetIndex < 0 || resolvedTargetIndex >= this.chatMessages.length) {
            for (let i = this.chatMessages.length - 1; i >= 0; i--) {
                if (this.chatMessages[i].sender === 'bot') {
                    resolvedTargetIndex = i;
                    break;
                }
            }
        }
        if (resolvedTargetIndex === -1) { return; }

        const targetMessage = this.chatMessages[resolvedTargetIndex];
        if (!targetMessage || targetMessage.sender !== 'bot') { return; }
        if (targetMessage.suggestions && targetMessage.suggestions.length > 0) { return; }

        this.chatService.getFollowUpSuggestions(
            this.chatHistory,
            this.lastAgentUsed,
            this.currentThreadId || null
        ).pipe(takeUntil(this.destroy$)).subscribe(suggestions => {
            if (suggestions.length === 0) { return; }
            this.chatMessages = this.chatMessages.map((msg, idx) =>
                idx === resolvedTargetIndex ? { ...msg, suggestions } : msg
            );
            this.suggestionsDisabled = false;
        });
    }

    private appendLatestTurnToChatHistory(answerText: string): void {
        const normalizedAnswer = this.normalizeIncomingMarkdown(answerText || '').trim();
        const userText = this.getLatestUserMessageText();

        if (!userText && !normalizedAnswer) {
            return;
        }

        const lastTurn = this.chatHistory[this.chatHistory.length - 1];
        if (lastTurn && lastTurn[0] === userText && lastTurn[1] === normalizedAnswer) {
            return;
        }

        this.chatHistory.push([userText, normalizedAnswer]);
    }

    private getLatestUserMessageText(): string {
        for (let i = this.chatMessages.length - 1; i >= 0; i--) {
            if (this.chatMessages[i].sender === 'user') {
                return (this.chatMessages[i].text || '').trim();
            }
        }

        return '';
    }

    private buildDrtParametersSummary(drtParameters: DrtEditablePayload): string {
        const previewFields = (drtParameters?.sections || [])
            .flatMap(section => section.fields || [])
            .slice(0, 4)
            .map(field => `${field.key}: ${field.value || 'N/A'}`);

        const previewText = previewFields.length > 0 ? ` ${previewFields.join('; ')}.` : '';
        return `DRT parameters extracted for review. Confirm and proceed to generate the DRT.${previewText}`;
    }

    /**
     * Handle API error
     */
    private handleApiError(error: any): void {
        const errorText = error?.error || error?.message || 'Sorry, I encountered an error while processing your request. Please try again.';

        // Create error message
        const errorMessage: ChatMessage = {
            id: 'error_' + Date.now(),
            text: `Sorry, I encountered an error while processing your request. ${errorText}`,
            sender: 'bot',
            timestamp: new Date(),
            type: 'text'
        };

        // Add to chat
        this.chatMessages.push(errorMessage);

        // Hide loading
        this.isLoading = false;
        this.hasActiveRequest = false;
        this.pendingPrompt = null;
        this.isReconnectingSocket = false;
        this.hasReceivedResponseForActiveRequest = false;
        this.processedResponseFingerprints.clear();
    }

    private buildResponseFingerprint(threadId: string, responseText: string, agentUsed: unknown): string {
        const normalizedThreadId = (threadId || this.currentThreadId || this.PENDING_THREAD_ID).trim();
        const normalizedAgent = typeof agentUsed === 'string' ? agentUsed.trim().toLowerCase() : '';
        const normalizedText = (responseText || '').trim();
        return `${normalizedThreadId}::${normalizedAgent}::${normalizedText}`;
    }

    private hasExistingDrtResultMessage(
        threadId: string,
        drtPayload: unknown
    ): boolean {
        const normalizedThreadId = (threadId || '').trim();
        const payloadSignature = drtPayload ? JSON.stringify(drtPayload) : '';

        for (let i = this.chatMessages.length - 1; i >= 0 && i >= this.chatMessages.length - 5; i--) {
            const message = this.chatMessages[i];
            if (!message || message.sender !== 'bot' || message.type !== 'drt-result-table') {
                continue;
            }

            if (normalizedThreadId && (message.threadId || '').trim() !== normalizedThreadId) {
                continue;
            }

            const existingPayload = message.documents?.[0]?.drtParameters;
            const existingPayloadSignature = existingPayload ? JSON.stringify(existingPayload) : '';
            if (payloadSignature && existingPayloadSignature && existingPayloadSignature === payloadSignature) {
                return true;
            }
        }

        return false;
    }

    private buildBotMessageFromResponseData(
        responseData: any,
        timestamp: Date,
        threadId: string,
        includeLatestRoutingFallback: boolean = false
    ): ChatMessage {
        const formattedResponse = this.normalizeIncomingMarkdown(responseData?.response || responseData?.ai_response || '');

        const derivedRouting = {
            primary_agent: responseData?.primary_agent,
            secondary_agents: responseData?.secondary_agents,
            confidence: responseData?.confidence_score,
            reasoning: responseData?.confidence_reasoning
        };

        const latestRouting = includeLatestRoutingFallback ? this.latestRouting : null;
        const hasDerivedRouting = Object.values(derivedRouting).some((value) => value !== undefined && value !== null && value !== '');
        const routing = responseData?.routing || (hasDerivedRouting ? derivedRouting : latestRouting || undefined);

        const primaryAgent = responseData?.agent_used
            || routing?.primary_agent
            || responseData?.primary_agent
            || '';

        const isEwoResponse =
            (routing?.primary_agent || '').toLowerCase() === 'ewo'
            || (responseData?.primary_agent || '').toLowerCase() === 'ewo'
            || (primaryAgent || '').toLowerCase().includes('ewo');

        const feedbackPayload = responseData?.feedback;
        const feedbackSelection: 'like' | 'dislike' | undefined =
            feedbackPayload?.sentiment === 'like' || feedbackPayload?.sentiment === 'dislike'
                ? feedbackPayload.sentiment
                : undefined;
        const feedbackSubmitted = !!feedbackSelection;

        const sharedFeedbackState = {
            feedbackSelection,
            feedbackSubmitted,
            feedbackDialogOpen: false
        };

        // DRT history: primary_agent is 'DRT Agent' and ai_response is the table object
        const isDrtResponse = (primaryAgent || '').toLowerCase().includes('drt');
        let rawAiResponse = responseData?.ai_response;
        // Backend persists the DRT table via json.dumps, so history returns it as a string.
        if (isDrtResponse && typeof rawAiResponse === 'string' && rawAiResponse.trim().startsWith('{')) {
            try {
                rawAiResponse = JSON.parse(rawAiResponse);
            } catch {
                // leave as plain text
            }
        }
        if (isDrtResponse && rawAiResponse && typeof rawAiResponse === 'object' && !Array.isArray(rawAiResponse)) {
            const drtPayload = this.extractGeneratedDrtTablePayload({ status: 'success', ...rawAiResponse });
            if (drtPayload) {
                return {
                    id: `${threadId}_${Date.now()}_${this.chatMessages.length}`,
                    text: 'Generated DRT Summary',
                    sender: 'bot',
                    timestamp,
                    type: 'drt-result-table',
                    documents: [
                        {
                            id: `drt_result_${Date.now()}`,
                            title: 'Generated DRT',
                            date: new Date(timestamp).toLocaleDateString(),
                            documentType: 'EWO',
                            summary: 'Generated DRT output',
                            selected: true,
                            drtParameters: drtPayload
                        }
                    ],
                    threadId,
                    agent: primaryAgent,
                    ...sharedFeedbackState
                };
            }
        }

        if (isEwoResponse) {
            // Check whether a CES secondary also ran alongside (and therefore the
            // Synthesizer appended an "## EWO Search Results" sentinel block).
            // Detect this from the text itself rather than routing.secondary_agents,
            // because secondary_agents is not persisted and is absent on history reload.
            const hasEwoSectionMarker = this.hasAppendedEwoSection(formattedResponse);
            const ewoCards = this.parseEwoMarkdownToCards(formattedResponse);
            // Extract filter fallback notice from agent_context if present
            const filterFallbackNotice: string =
                responseData?.agent_context?.agent_logs?.ewo_agent?.filter_fallback_notice || '';
            if (ewoCards.length > 0 && !hasEwoSectionMarker) {
                // Pure standalone EWO response — show document cards only
                const leadingNotice = this.extractEwoLeadingNotice(formattedResponse);
                return {
                    id: `${threadId}_${Date.now()}_${this.chatMessages.length}`,
                    text: leadingNotice || `Found ${ewoCards.length} relevant EWO document(s)`,
                    sender: 'bot',
                    timestamp,
                    type: 'document-results',
                    threadId,
                    agent: primaryAgent,
                    routing,
                    citations: responseData?.citations || {},
                    documents: ewoCards,
                    filterFallbackNotice: filterFallbackNotice || undefined,
                    ...sharedFeedbackState
                };
            }
            if (ewoCards.length > 0 && hasEwoSectionMarker) {
                // Combined EWO-primary + CES-secondary: synthesis text + EWO cards
                const textOnly = this.extractSynthesisText(formattedResponse);
                return {
                    id: `${threadId}_${Date.now()}_${this.chatMessages.length}`,
                    text: textOnly,
                    sender: 'bot',
                    timestamp,
                    type: 'text',
                    isMarkdown: true,
                    threadId,
                    agent: primaryAgent,
                    routing,
                    citations: responseData?.citations || {},
                    documents: ewoCards,
                    ...sharedFeedbackState
                };
            }
        }

        // Combined CES-primary + EWO-secondary: the Synthesizer appends a
        // "## EWO Search Results" section containing the original EWO markdown.
        // Detect this by the text marker itself — NOT by routing.secondary_agents,
        // because secondary_agents is not persisted to the conversation history store
        // and therefore is always absent on history reload.
        const hasEwoSection = this.hasAppendedEwoSection(formattedResponse);
        if (hasEwoSection) {
            const ewoCards = this.parseEwoMarkdownToCards(formattedResponse);
            if (ewoCards.length > 0) {
                const textOnly = this.extractSynthesisText(formattedResponse);
                return {
                    id: `${threadId}_${Date.now()}_${this.chatMessages.length}`,
                    text: textOnly,
                    sender: 'bot',
                    timestamp,
                    type: 'text',
                    isMarkdown: true,
                    threadId,
                    agent: primaryAgent,
                    routing,
                    citations: responseData?.citations || {},
                    documents: ewoCards,
                    ...sharedFeedbackState
                };
            }
        }

        return {
            id: `${threadId}_${Date.now()}_${this.chatMessages.length}`,
            text: formattedResponse,
            sender: 'bot',
            timestamp,
            type: 'text',
            isMarkdown: true,
            threadId,
            agent: primaryAgent,
            routing,
            citations: responseData?.citations || {},
            ...sharedFeedbackState
        };
    }

    private buildUserContext(): { top_k: number; search_mode: string; ewo_qna_mode: boolean; selected_agent: string } {
        const topK = Number(this.searchSettings?.numberOfDocuments);
        const normalizedTopK = Number.isFinite(topK) ? Math.max(1, Math.min(10, topK)) : 5;
        const searchMode = this.searchSettings?.searchType === 'detailed' ? 'detailed' : 'quick';

        return {
            top_k: normalizedTopK,
            search_mode: searchMode,
            ewo_qna_mode: this.searchSettings?.selectedAgent === 'ewo_qna',
            selected_agent: this.searchSettings?.selectedAgent || 'auto'
        };
    }

    private getFirstPdfFile(files: File[]): File | null {
        if (!files || files.length === 0) {
            return null;
        }

        const byMime = files.find(file => file.type === 'application/pdf');
        if (byMime) {
            return byMime;
        }

        const byExtension = files.find(file => file.name.toLowerCase().endsWith('.pdf'));
        return byExtension || null;
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const result = reader.result;
                if (typeof result !== 'string') {
                    reject(new Error('Invalid file read result'));
                    return;
                }

                const base64 = result.includes(',') ? result.split(',')[1] : result;
                resolve(base64);
            };

            reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    }

    /**
     * Animate welcome text with typewriter effect
     */
    private animateWelcomeText(): void {
        // Clear any existing interval before starting a new one.
        // Without this, navigating back to home starts a second interval while the
        // first is still running, causing characters to be appended twice (garbled text).
        if (this.animationInterval !== null) {
            clearInterval(this.animationInterval);
            this.animationInterval = null;
        }

        this.animatedWelcomeText = '';
        this.isAnimating = true;
        this.animationComplete = false;
        let index = 0;

        this.animationInterval = setInterval(() => {
            if (index < this.fullText.length) {
                this.animatedWelcomeText += this.fullText.charAt(index);
                index++;
            } else {
                clearInterval(this.animationInterval!);
                this.animationInterval = null;
                this.isAnimating = false;
                this.animationComplete = true;
            }
        }, this.animationSpeed);
    }

    private extractGeneratedDrtTablePayload(drtResult: any): Record<string, any> | null {
        if (!drtResult || drtResult.status !== 'success') {
            return null;
        }
        // Meta-keys that are NOT DRT table sections (same list as isValidSection in the table component).
        const META_KEYS = new Set([
            'status', 'response', 'agent', 'thread_id',
            'excel_file', 'reasoning_log', 'message', 'data'
        ]);
        // If the result itself contains real section keys, use it directly.
        const hasSections = Object.keys(drtResult).some(k => !META_KEYS.has(k.toLowerCase()));
        if (hasSections) {
            return drtResult;
        }
        // Fall back to a nested `data` envelope if present (future-proofing).
        if (drtResult.data && typeof drtResult.data === 'object' && !Array.isArray(drtResult.data)) {
            return drtResult.data;
        }
        return null;
    }

    private buildDrtEditablePayload(drtData: any): DrtEditablePayload {
        const processInfo = drtData?.process_info || {};
        const scopeDocInfo = drtData?.scope_parameters || {};
        const dropdownOptions = drtData?.dropdown_options || {};
        const scopeReviewFields: string[] = drtData?.scope_review_fields || [];
        const strictFields = new Set<string>(drtData?.strict_fields || []);
        // Applicability is Yes / No / Not specified; legacy wording from older payloads is mapped onto those.
        const APPLICABILITY_KEYS = new Set(['heat_tracing', 'insulation', 'coating', 'labeling']);
        const toApplicabilityChoice = (raw: unknown): string => {
            const text = String(raw ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
            if (!text) {
                return 'Not specified';
            }
            if (['no', 'n/a', 'na', 'none', 'not needed'].includes(text) || /\bnot (applicable|required|in scope|needed)\b/.test(text)) {
                return 'No';
            }
            return /\b(applicable|required|yes)\b/.test(text) ? 'Yes' : 'Not specified';
        };

        const getDropdownOptions = (key: string): string[] => {
            const rawOptions = dropdownOptions?.[key];
            if (!Array.isArray(rawOptions)) {
                return [];
            }

            return rawOptions
                .map(option => option === null || option === undefined ? '' : String(option).trim())
                .filter(option => option.length > 0);
        };

        // Keys in scope_parameters already shown in the Scope Summary / process_info card, or internal resolver inputs not meant for user review.
        const SCOPE_PARAMS_IN_REVIEW = new Set([
            'service_process', 'api_pipe_class', 'environmental_scc',
            'sour', 'hydrotest', 'code_work',
            'weld_type', 'min_preheat_f', 'max_interpass_f', 'is_p5ab_material',
        ]);
        const DIRECT_DRT_SCOPE_KEYS = new Set([
            'nde_rt_paut', 'nde_rt_paut_pct', 'nde_pt_mt', 'nde_pt_mt_pct',
            'nde_hardness_testing', 'nde_hardness_bhn', 'nde_hardness_pct',
            'nde_pmi_required', 'nde_ferrite_test',
            'pwht_required', 'pwht_temperature_f', 'pwht_duration',
            'bakeout_required', 'bakeout_type', 'bakeout_temperature', 'bakeout_duration',
            'mdmt_mpt_f', 'dht_value', 'isr_value',
            'insulation_number', 'insulation_standard_drawing',
            'coating_system', 'top_coat_color', 'applicable_design_code',
            'nde_in_lieu_of_hydrotest', 'hydrotest_fluid',
        ]);
        // The 5 most decision-relevant scope params — shown up front; everything else is
        // collapsed behind a "See more" toggle in the Scope Information section.
        const IMPORTANT_SCOPE_PARAM_KEYS = new Set([
            'piping_or_equipment', 'equipment_tag_number',
            'refinery', 'nde_in_lieu_of_hydrotest',
        ]);

        // Friendly display names for raw scope_parameters/process_info keys.
        const SCOPE_PARAM_LABELS: Record<string, string> = {
            p_number_1: 'P No. 1',
            p_number_2: 'P No. 2',
            piping_or_equipment: 'Piping or Equipment',
            equipment_tag_number: 'Equipment Tag Number',
            refinery: 'Refinery',
            nde_in_lieu_of_hydrotest: 'NDE In-Lieu of Hydrotest',
            vessel_new_or_old: 'Vessel New or Old',
            pipe_material_or_equipment_material: 'Pipe/Equipment Material',
            pipe_thickness_inches: 'Pipe Thickness (in)',
            equipment_thickness_inches: 'Equipment Thickness (in)',
            pipe_diameter_nps: 'Pipe Diameter (NPS)',
            line_or_equipment_tag: 'Line/Equipment Tag',
            heat_tracing: 'Heat Tracing',
            insulation: 'Insulation',
            coating: 'Coating',
            labeling: 'Labeling',
            operating_temperature_f: 'Operating Temperature (\u00b0F)',
            process_pressure_psig: 'Process Pressure (psig)',
            hydrotest_fluid: 'Hydrotest Fluid',
            bakeout_required: 'Bakeout Required',
            pwht_required: 'PWHT Required',
            pwht_temperature_f: 'PWHT Temperature (\u00b0F)',
            pwht_duration: 'PWHT Duration',
            nde_rt_paut: 'NDE RT/PAUT',
            nde_rt_paut_pct: 'NDE RT/PAUT %',
            nde_pt_mt: 'NDE PT/MT',
            nde_pt_mt_pct: 'NDE PT/MT %',
            nde_hardness_testing: 'NDE Hardness Testing',
            nde_pmi_required: 'NDE PMI Required',
            nde_ferrite_test: 'NDE Ferrite Test',
        };

        // Subheadings for the "See more" scope params, in display order; unlisted keys fall under "Other".
        const SCOPE_PARAM_GROUPS: Array<{ title: string; keys: string[] }> = [
            { title: 'Asset and Job', keys: ['asset_type_or_component', 'line_or_equipment_tag', 'vessel_new_or_old', 'refinery_unit'] },
            { title: 'Material and Welding', keys: ['p_number_1', 'p_number_2', 'pipe_material_or_equipment_material', 'pipe_spec_number'] },
            { title: 'Dimensions', keys: ['pipe_diameter_nps', 'pipe_thickness_inches', 'equipment_thickness_inches'] },
            { title: 'Operating Conditions', keys: ['operating_temperature_f', 'process_pressure_psig'] },
            { title: 'Coating, Insulation and Heat Tracing', keys: ['heat_tracing', 'insulation', 'coating', 'labeling', 'coating_system', 'top_coat_color', 'insulation_number', 'insulation_standard_drawing'] },
            { title: 'NDE', keys: [
                'nde_rt_paut', 'nde_rt_paut_pct', 'nde_pt_mt', 'nde_pt_mt_pct',
                'nde_hardness_testing', 'nde_hardness_bhn', 'nde_hardness_pct',
                'nde_pmi_required', 'nde_ferrite_test', 'nde_in_lieu_of_hydrotest',
                'hydrotest_fluid', 'mdmt_mpt_f',
            ] },
            { title: 'PWHT', keys: ['pwht_required', 'pwht_temperature_f', 'pwht_duration'] },
            { title: 'Bakeout', keys: ['bakeout_required', 'bakeout_type', 'bakeout_temperature', 'bakeout_duration'] },
            { title: 'DHT / ISR', keys: ['dht_value', 'isr_value'] },
            { title: 'Other', keys: ['applicable_design_code'] },
        ];
        const SCOPE_PARAM_GROUP_KEYS = new Map<string, { title: string; order: number }>();
        SCOPE_PARAM_GROUPS.forEach((group, groupIndex) => group.keys.forEach((key, keyIndex) =>
            SCOPE_PARAM_GROUP_KEYS.set(key, { title: group.title, order: groupIndex * 1000 + keyIndex })));
        const OTHER_GROUP_ORDER = SCOPE_PARAM_GROUPS.length * 1000;

        // Fallback prettifier for any key not covered above (snake_case -> Title Case).
        const prettifyKey = (key: string): string => key
            .split('_')
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        const toSection = (
            id: 'process_info' | 'scope_parameters' | 'scope_review',
            title: string,
            source: Record<string, unknown>,
            isScopeReview: boolean = false
        ): DrtEditableSection => ({
            id,
            title,
            isScopeReview,
            expanded: false,
            // Applicability must always be reviewable, even when the scope did not state it.
            fields: Object.entries(id === 'scope_parameters'
                ? { ...Object.fromEntries([...APPLICABILITY_KEYS].map(k => [k, ''])), ...source }
                : source)
                .filter(([key]) => key !== 'confidence_notes')
                // Internal resolver metadata (e.g. _scope_doc_citations) must never be
                // user-editable; keeping it out of the form prevents object->string corruption.
                .filter(([key]) => id !== 'scope_parameters' || !key.startsWith('_'))
                .filter(([key]) => id !== 'scope_parameters' || !SCOPE_PARAMS_IN_REVIEW.has(key))
                .filter(([key, value]) => id !== 'scope_parameters'
                    || key !== 'furnace_pwht_required'
                    || !['', 'no', 'n/a'].includes(String(value ?? '').trim().toLowerCase()))
                .filter(([key, value]) => id !== 'scope_parameters'
                    || !DIRECT_DRT_SCOPE_KEYS.has(key)
                    || (value !== null && value !== undefined && String(value).trim() !== ''))
                .map(([key, rawValue]) => {
                    const value = id === 'scope_parameters' && APPLICABILITY_KEYS.has(key) ? toApplicabilityChoice(rawValue) : rawValue;
                    return { key, value };
                })
                .map(({ key, value }) => ({
                    key,
                    value: value === null || value === undefined ? '' : String(value),
                    committedValue: value === null || value === undefined ? '' : String(value),
                    dropdownOptions: getDropdownOptions(key),
                    strict: strictFields.has(key),
                    label: SCOPE_PARAM_LABELS[key] || prettifyKey(key),
                    // Fields outside scope_parameters (process_info/scope_review) are always shown.
                    important: id !== 'scope_parameters' || IMPORTANT_SCOPE_PARAM_KEYS.has(key),
                    group: id === 'scope_parameters' && !IMPORTANT_SCOPE_PARAM_KEYS.has(key)
                        ? (SCOPE_PARAM_GROUP_KEYS.get(key)?.title ?? 'Other')
                        : undefined
                }))
                .sort((a, b) => {
                    if (id !== 'scope_parameters') {
                        return 0;
                    }
                    const orderOf = (field: DrtEditableField): number => field.important
                        ? -1
                        : (SCOPE_PARAM_GROUP_KEYS.get(field.key)?.order ?? OTHER_GROUP_ORDER);
                    return orderOf(a) - orderOf(b);
                })
        });

        const sections: DrtEditableSection[] = [];

        // Scope review fields shown as a highlighted card first
        if (scopeReviewFields.length > 0 && Object.keys(processInfo).length > 0) {
            sections.push(toSection('scope_review', 'Scope Summary — Review Before Generating DRT', processInfo, true));
        } else if (Object.keys(processInfo).length > 0) {
            sections.push(toSection('process_info', 'Process Information', processInfo));
        }

        // Full scope_parameters (all resolver inputs) — keys already shown above are hidden
        if (Object.keys(scopeDocInfo).length > 0) {
            sections.push(toSection('scope_parameters', 'Scope Information', scopeDocInfo));
        }

        return {
            status: drtData?.status,
            thread_id: drtData?.thread_id,
            sections: sections.filter(s => s.fields.length > 0),
            scopeReviewFields,
        };
    }

    private buildUpdatedDrtRawData(
        originalData: any,
        processInfo: Record<string, string>,
        scopeDocInfo: Record<string, string>,
        threadId: string
    ): any {
        const originalProcessInfo = originalData?.process_info || {};
        const originalScopeDocInfo = originalData?.scope_parameters || {};

        return {
            ...(originalData || {}),
            thread_id: threadId,
            process_info: {
                ...originalProcessInfo,
                ...processInfo,
                confidence_notes: originalProcessInfo.confidence_notes || {}
            },
            scope_parameters: {
                ...originalScopeDocInfo,
                ...scopeDocInfo,
                confidence_notes: originalScopeDocInfo.confidence_notes || {}
            }
        };
    }

    public async onDrtGenerateRequested(event: DrtGenerateEvent): Promise<void> {
        const targetMessage = event.message;
        if (!targetMessage) {
            return;
        }

        const threadId = targetMessage.threadId || this.currentThreadId || this.PENDING_THREAD_ID;
        const updatedDrtData = this.buildUpdatedDrtRawData(
            targetMessage.drtRawData,
            event.process_info,
            event.scope_parameters,
            threadId
        );

        let pdfFileB64 = targetMessage.drtSourcePdfFileB64 || this.lastDrtPdfFileB64;
        if (!pdfFileB64) {
            const selectedPdf = this.getFirstPdfFile(this.selectedFileObjects);
            if (selectedPdf) {
                try {
                    pdfFileB64 = await this.fileToBase64(selectedPdf);
                    this.lastDrtPdfFileB64 = pdfFileB64;
                    targetMessage.drtSourcePdfFileB64 = pdfFileB64;
                } catch {
                    // Handled below with unified missing-PDF branch
                }
            }
        }

        // Ensure WebSocket is reconnected before sending DRT step 2
        if (this.needsReconnection && this.pendingThreadId) {
            await this.ensureWebSocketReconnected();
        }

        this.isLoading = true;
        this.hasActiveRequest = true;
        this.hasReceivedResponseForActiveRequest = false;
        this.processedResponseFingerprints.clear();

        this.chatMessages.push({
            id: this.generateId(),
            text: 'Confirm and proceed',
            sender: 'user',
            timestamp: new Date(),
            type: 'text',
            threadId: threadId
        });
        this.persistMessagesToStorage();

        if (!pdfFileB64) {
        }

        const sent = this.chatService.sendMessageOverWebSocket(
            '',
            this.chatHistory,
            threadId,
            this.buildUserContext(),
            {
                pdf_file_b64: pdfFileB64 || undefined,
                verified_process_info: event.process_info,
                scope_parameters: event.scope_parameters,
                drt_parameters_data: updatedDrtData
            }
        );

        if (!sent) {
            targetMessage.drtGeneratePending = false;
            this.pendingPrompt = '';
            this.pendingDrtPayload = {
                pdf_file_b64: pdfFileB64 || undefined,
                verified_process_info: event.process_info,
                scope_parameters: event.scope_parameters,
                drt_parameters_data: updatedDrtData
            };
            this.isReconnectingSocket = true;
            this.initializeWebSocketForCurrentThread(threadId);
            return;
        }

        this.persistMessagesToStorage();
    }

    public onEwoShowMoreRequested(event: EwoShowMoreEvent): void {
        const threadId = event?.message?.threadId || this.currentThreadId || this.PENDING_THREAD_ID;
        const sourceConversationId = event?.message?.conversationId;
        if (!sourceConversationId) {
            this.handleApiError({ message: 'The originating EWO search is missing a conversation id.' });
            return;
        }
        const userContext = this.buildUserContext();
        const additionalTopK = userContext.top_k || 10;

        // Mirror normal submit UX: show the user intent in the chat stream.
        this.chatMessages.push({
            id: this.generateId(),
            text: 'show more',
            sender: 'user',
            timestamp: new Date(),
            type: 'text',
            threadId: threadId,
        });
        this.persistMessagesToStorage();

        this.isLoading = true;
        this.hasActiveRequest = true;
        this.hasReceivedResponseForActiveRequest = false;
        this.processedResponseFingerprints.clear();

        // Queue payload first so transient WS send errors can be safely retried.
        this.pendingPrompt = 'show more';
        this.pendingDrtPayload = {
            search_more_ewos: true,
            source_conversation_id: sourceConversationId,
            top_k: additionalTopK
        };

        const sent = this.chatService.sendMessageOverWebSocket(
            'show more',
            this.chatHistory,
            threadId,
            userContext,
            {
                search_more_ewos: true,
                source_conversation_id: sourceConversationId,
                top_k: additionalTopK
            }
        );

        if (sent) {
            this.pendingPrompt = null;
            this.pendingDrtPayload = null;
        } else {
            this.isReconnectingSocket = true;
            this.initializeWebSocketForCurrentThread(threadId);
        }
    }

    //*************************************************************************
    //  Feedback Methods
    //*************************************************************************
    public onFeedbackDialogStateChanged(isOpen: boolean): void {
        this.feedbackDialogOpen = isOpen;
    }

    public onFeedbackSubmitted(event: ChatFeedbackEvent): void {
        const threadId = event.message.threadId || this.currentThreadId;

        if (!threadId) {
            return;
        }

        // Find the original user question that corresponds to this bot response
        const userQuestion = this.findUserQuestionForBotMessage(event.message, event.messageIndex);
        
        if (!userQuestion) {
            return;
        }

        const feedbackPayload: FeedbackRequestPayload = {
            thread_id: threadId,
            question: userQuestion,
            conversation_id: event.message.conversationId,
            sentiment: event.selection,
            documents_relevant: this.mapDocumentsRelevant(event.relevance),
            answer_aligned: this.mapAnswerAligned(event.completeness),
            response_rating: event.rating,
            additional_comments: event.comments || ''
        };

        this.chatService.submitFeedback(feedbackPayload).subscribe({
            error: (error) => {
                console.error('Feedback submission error:', error);
            }
        });
    }

    /** Finds the user question preceding a given bot response message in chat history. */
    private findUserQuestionForBotMessage(targetMessage: ChatMessage, fallbackMessageIndex: number): string | null {
        let resolvedIndex = Number.isInteger(fallbackMessageIndex) ? fallbackMessageIndex : -1;

        if (resolvedIndex < 0 || resolvedIndex >= this.chatMessages.length) {
            resolvedIndex = this.chatMessages.findIndex(message =>
                (targetMessage.id && message.id === targetMessage.id) || message === targetMessage
            );
        }

        if (resolvedIndex < 0) {
            return null;
        }

        // Search backwards from the bot message to find the preceding user message
        for (let i = resolvedIndex - 1; i >= 0; i--) {
            const message = this.chatMessages[i];
            if (message.sender === 'user' && message.type === 'text') {
                return message.text;
            }
        }

        return null;
    }

    private mapDocumentsRelevant(relevance?: 'very' | 'partial' | 'not'): 'Yes' | 'Partially' | 'No' | undefined {
        if (relevance === 'very') {
            return 'Yes';
        }
        if (relevance === 'partial') {
            return 'Partially';
        }
        if (relevance === 'not') {
            return 'No';
        }
        return undefined;
    }

    private mapAnswerAligned(completeness?: 'complete' | 'incomplete' | 'unsure'): 'Yes' | 'Partially' | 'No' | undefined {
        if (completeness === 'complete') {
            return 'Yes';
        }
        if (completeness === 'incomplete') {
            return 'No';
        }
        if (completeness === 'unsure') {
            return 'Partially';
        }
        return undefined;
    }

    /**
     * Markdown conversion is now handled by MarkdownService and MarkdownPipe in templates.
     */

    /** Generates a unique ID for messages. */
    private generateId(): string {
        return 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /** Normalizes incoming markdown payload, handling escaped newlines/tabs from JSON content. */
    private normalizeIncomingMarkdown(response: unknown): string {
        if (typeof response === 'object' && response !== null) {
            const responseObject = response as Record<string, unknown>;
            const candidateKeys = ['response', 'answer', 'content', 'text', 'final_response'];

            for (const key of candidateKeys) {
                const candidate = responseObject[key];
                if (typeof candidate === 'string' && candidate.trim().length > 0) {
                    return this.normalizeIncomingMarkdown(candidate);
                }
            }

            try {
                const jsonFallback = JSON.stringify(responseObject, null, 2);
                return jsonFallback && jsonFallback.trim().length > 0 ? jsonFallback : 'No response generated.';
            } catch {
                return 'No response generated.';
            }
        }

        if (typeof response !== 'string') {
            return response === null || response === undefined ? 'No response generated.' : String(response);
        }

        let normalized = response.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

        normalized = normalized.replace(/\\n/g, '\n');

        if (normalized.includes('\\t')) {
            normalized = normalized.replace(/\\t/g, '    ');
        }

        return normalized.trim().length > 0 ? normalized : 'No response generated.';
    }

    private parseEwoMarkdownToCards(markdown: string): ChatDocumentCard[] {
        const cards: ChatDocumentCard[] = [];

        // Split on horizontal rules (allow surrounding blank lines)
        const blocks = markdown.split(/\n[ \t]*---[ \t]*\n/);
        const leadingSharedBlocks: string[] = [];
        const trailingSharedBlocks: string[] = [];
        let hasSeenDocumentBlock = false;

        for (const block of blocks) {
            const trimmed = block.trim();
            if (!trimmed) { continue; }

            const isDocumentBlock = !!this.parseEwoHeader(trimmed);
            if (isDocumentBlock) {
                hasSeenDocumentBlock = true;
                continue;
            }

            if (!hasSeenDocumentBlock) {
                leadingSharedBlocks.push(trimmed);
                continue;
            }

            trailingSharedBlocks.push(trimmed);
        }

        for (const block of blocks) {
            const trimmed = block.trim();
            if (!trimmed) { continue; }

            const parsedHeader = this.parseEwoHeader(trimmed);
            if (!parsedHeader) { continue; }

            const docName = parsedHeader.docName;
            const docUrl = parsedHeader.docUrl;
            const statusStr = parsedHeader.statusStr;

            // Extract single-line field value
            const field = (name: string): string => {
                const escaped = name.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&');
                const m = trimmed.match(new RegExp(`\\*\\*${escaped}:\\*\\*\\s*([^\\n]+)`));
                return m ? m[1].replace(/\s{2,}$/, '').trim() : '';
            };

            // Extract multi-line field value (until next ** field, ---, or end)
            const multiField = (name: string): string => {
                const escaped = name.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&');
                const m = trimmed.match(new RegExp(`\\*\\*${escaped}:\\*\\*\\s*([\\s\\S]+?)(?=\\n\\*\\*|\\n---|$)`));
                return m ? m[1].replace(/\s{2,}$/, '').trim() : '';
            };

            const ewoId       = field('EWO ID');
            const title       = field('Title') || docName;
            const facility    = field('Facility/Business Unit') || field('Facility');
            const discipline  = field('Discipline');
            const category    = field('Category');
            const subCategory = field('Sub-Category') || field('Sub Category');
            const execDate    = field('Date of Execution') || field('Date');
            const abstract    = multiField('Abstract');
            const scopeOfWork = multiField('Scope of Work');
            const keyLearnings= multiField('Key Learnings') || multiField('Key Learning');
            const equipType   = field('Equipment Type');
            const equipTags   = field('Equipment Tags') || field('Equipment Tag');
            const processMed  = field('Process Medium');
            const drtParametersRaw = multiField('DRT Parameters') || field('DRT Parameters');
            const drtParameters = this.parseDrtParameters(drtParametersRaw);
            const previewContentParts = [
                ...leadingSharedBlocks,
                trimmed,
                ...trailingSharedBlocks
            ].filter(part => part.trim().length > 0);
            const previewContent = previewContentParts.join('\n\n---\n\n');

            cards.push({
                id:             ewoId || docName,
                title:          title,
                date:           execDate,
                documentType:   'EWO',
                summary:        abstract || scopeOfWork || '',
                ewoId:          ewoId,
                facility:       facility,
                discipline:     discipline,
                category:       category,
                subCategory:    subCategory,
                equipmentType:  equipType,
                equipmentTags:  equipTags,
                processMedium:  processMed,
                scopeOfWork:    scopeOfWork,
                keyLearnings:   keyLearnings,
                abstract:       abstract,
                approvalStatus: statusStr,
                previewUrl:     docUrl,
                fullContent:    trimmed,
                previewContent: previewContent,
                drtParameters:  drtParameters || undefined
            });
        }

        return cards;
    }

    private parseEwoHeader(block: string): { docName: string; docUrl: string; statusStr: string } | null {
        // Primary format: ### 1. [Document Name](url) Approved
        const markdownHeaderMatch = block.match(/###\s+(?:\d+\.\s+)?\[([^\]]+)\]\(([^)]+)\)\s*(.*)/m);
        if (markdownHeaderMatch) {
            return {
                docName: this.cleanEwoHeaderText(markdownHeaderMatch[1]),
                docUrl: markdownHeaderMatch[2].trim(),
                statusStr: this.cleanEwoHeaderText(markdownHeaderMatch[3])
            };
        }

        // Alternate format from EWO responses: ### 1. Name <sup><a href='url'>[1]</a></sup> Approved
        const htmlAnchorHeaderMatch = block.match(/###\s+(?:\d+\.\s+)?(.+?)\s*<sup>\s*<a\s+[^>]*href=(['"])([^'"]+)\2[^>]*>[\s\S]*?<\/a>\s*<\/sup>\s*(.*)/im);
        if (htmlAnchorHeaderMatch) {
            return {
                docName: this.cleanEwoHeaderText(htmlAnchorHeaderMatch[1]),
                docUrl: htmlAnchorHeaderMatch[3].trim(),
                statusStr: this.cleanEwoHeaderText(htmlAnchorHeaderMatch[4])
            };
        }

        return null;
    }

    private cleanEwoHeaderText(value: string): string {
        if (!value) {
            return '';
        }

        // Remove citation superscripts and any remaining HTML tags from legacy header formats.
        return value
            .replace(/<sup>[\s\S]*?<\/sup>/gi, ' ')
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * Strip the "## EWO Search Results" section (and everything after it) that
     * the Synthesizer appends to combined CES+EWO responses.  The stripped text
     * is the synthesised analysis shown as the message body; the EWO section is
     * parsed separately into document cards.
     */
    /**
     * Returns true when the response contains the Synthesizer-appended
     * "## EWO Search Results" sentinel block.
     * Uses a lenient pattern to handle whitespace variation after normalisation.
     */
    private hasAppendedEwoSection(markdown: string): boolean {
        return /\n[ \t]*---[ \t]*\n[\s\S]*?##\s+EWO Search Results/i.test(markdown);
    }

    /**
     * Extract leading text (notice / count line) that appears before the first
     * EWO card block (i.e. before the first '---' separator that precedes a
     * '### N. [Title]' line).  Used to preserve the date-fallback notice in
     * standalone EWO responses where only document cards are displayed.
     */
    private extractEwoLeadingNotice(markdown: string): string {
        const match = markdown.match(/^([\s\S]*?)\n[ \t]*---[ \t]*\n/);
        if (match && match[1].trim()) {
            return match[1].trim();
        }
        return '';
    }

    private extractSynthesisText(markdown: string): string {
        // The Synthesizer appends: \n\n---\n\n## EWO Search Results\n\n...
        // Use a lenient pattern: any ---  followed (possibly with blank lines) by
        // ## EWO Search Results, to survive whitespace normalisation on history reload.
        const match = markdown.match(/\n[ \t]*---[ \t]*\n[\s]*##\s+EWO Search Results/i);
        if (match && match.index !== undefined) {
            return markdown.substring(0, match.index).trim();
        }
        return markdown;
    }

    private parseDrtParameters(raw: string): Record<string, any> | null {
        if (!raw || !raw.trim()) {
            return null;
        }

        const candidates = this.getDrtJsonCandidates(raw);

        for (const candidate of candidates) {
            try {
                const parsed = JSON.parse(candidate);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    return parsed as Record<string, any>;
                }
            } catch {
            }
        }

        return null;
    }

    private getDrtJsonCandidates(raw: string): string[] {
        const cleaned = raw
            .trim()
            .replace(/^```json\s*/i, '')
            .replace(/^```\s*/i, '')
            .replace(/\s*```$/, '')
            .trim();

        const unescaped = cleaned
            .replace(/\\"/g, '"')
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false');

        const normalizedQuotes = unescaped
            .replace(/'([^']*)'/g, '"$1"');

        return [cleaned, unescaped, normalizedQuotes].filter((value, index, arr) => value.length > 0 && arr.indexOf(value) === index);
    }

}